import React, { Component, Fragment } from "react";
import { inject, observer } from "mobx-react";
import axios from "axios";
import ColorHash from "color-hash";
import contrast from "contrast";
import { Button, Icon, Label, Popup, Rating, Segment, Table } from "semantic-ui-react";
import * as R from "ramda";
import Sim from "./Sim";
import { getBotConfig } from "../utils/botUtils";
import { formatPercent } from "../utils/numberFormat";
import { getAuthHttpHeaders } from "../auth";
import { formatDateFromApi, formatDateTimeFromApi, instantFromApi } from "../utils/dateTime";
import CurrencyPair from "../components/CurrencyPair";
import SimsCharts from "./SimsCharts";

@inject("store")
@observer
class Sims extends Component {
  state = {
    column: null,
    sims: null,
    direction: "ascending",
    filters: [],
    ratings: {}, // id => rating
    hiddenColumns: [
      "botId",
      "type",
      "period",
      "config.maxCurrencyTrades",
      "config.currencyPair",
      "config.sticksCount",
      "config.peakChangePct",
      "createdAt.epochSecond",
    ],
  };

  constructor(props) {
    super(props);

    // eslint-disable-next-line
    this.state.hiddenColumns = localStorage.getItem("sims-hidden-columns")
      ? JSON.parse(localStorage.getItem("sims-hidden-columns"))
      : this.state.hiddenColumns;
  }

  componentDidMount() {
    this.load();
    this.refreshTimer = setInterval(() => {
      this.state.autoRefresh && this.load();
    }, 60 * 1000);
  }

  componentWillUnmount() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
    }
  }

  load = async () => {
    const { simGroupId } = this.props;

    this.setState({ loading: true });

    if (this.simComponent) {
      this.sim.wrappedInstance.load();
    }

    try {
      let axiosRequest = axios.create({
        baseURL: "api",
        timeout: 30 * 1000,
        headers: {
          Accept: "application/json",
          ...getAuthHttpHeaders(),
        },
        params: {
          simGroupId,
        },
      });

      let response = await axiosRequest.get("sim/summaries");
      // console.log('botInstance/summaries response', response);
      this.setState({ sims: response.data, column: null });

      if (this.state.selSim) {
        for (const sim of response.data) {
          if (sim.id === this.state.selSim.id) {
            this.setState({ selSim: sim });
            break;
          }
        }
      }
    } finally {
      this.setState({ loading: false });
    }
  };

  handleSort = clickedColumn => () => {
    const { column, sims, direction } = this.state;

    if (column !== clickedColumn) {
      this.setState({
        column: clickedColumn,
        sims: R.sortBy(R.path(clickedColumn.split(".")))(sims),
        direction: "ascending",
      });

      return;
    }

    this.setState({
      sims: sims.reverse(),
      direction: direction === "ascending" ? "descending" : "ascending",
    });
  };

  SortedHeaderCell = ({ columnKey, children }) => {
    const { column, direction, hiddenColumns } = this.state;
    return (
      !hiddenColumns.includes(columnKey) && (
        <Table.HeaderCell
          sorted={column === columnKey ? direction : null}
          onClick={this.handleSort(columnKey)}
          style={{ verticalAlign: "top" }}
        >
          <Icon
            name="hide"
            title="Hide column"
            onClick={e => {
              e.stopPropagation();
              const newHiddenColumns = [...hiddenColumns, columnKey];
              localStorage.setItem("sims-hidden-columns", JSON.stringify(newHiddenColumns));
              this.setState({ hiddenColumns: newHiddenColumns });
            }}
          />
          <br />
          {children}
        </Table.HeaderCell>
      )
    );
  };

  ValueCell = props => {
    const { columnKey, sim, ...cellProps } = props;
    const value = columnKey && R.path(columnKey.split("."))(sim);
    const backgroundColor = new ColorHash({ lightness: 0.8 }).hex(props.children);
    const color = contrast(backgroundColor) === "light" ? "#000" : "#fff";
    const filter = columnKey && { columnKey, mode: "=", value };
    const valueIsNumber = Number(value) === value;
    const { hiddenColumns } = this.state;
    return (
      !hiddenColumns.includes(columnKey) && (
        <Table.Cell {...cellProps} style={{ ...props.style, backgroundColor, color }}>
          {columnKey ? (
            <Popup
              hoverable
              flowing
              hideOnScroll
              on="click"
              trigger={<div>{props.children}</div>}
              content={
                <div>
                  <Button content="==" primary onClick={() => this.addFilter({ ...filter, mode: "==" })} />
                  <Button content="<>" primary onClick={() => this.addFilter({ ...filter, mode: "<>" })} />
                  {valueIsNumber && (
                    <Fragment>
                      <Button content="<" onClick={() => this.addFilter({ ...filter, mode: "<" })} />
                      <Button content="<=" onClick={() => this.addFilter({ ...filter, mode: "<=" })} />
                      <Button content=">=" onClick={() => this.addFilter({ ...filter, mode: ">=" })} />
                      <Button content=">" onClick={() => this.addFilter({ ...filter, mode: ">" })} />
                    </Fragment>
                  )}
                </div>
              }
            />
          ) : (
            props.children
          )}
        </Table.Cell>
      )
    );
  };

  addFilter = filter => {
    this.setState({ filters: [...this.state.filters, filter] });
  };

  removeFilter = filter => {
    this.setState({ filters: this.state.filters.filter(iFilter => !R.equals(iFilter, filter)) });
  };

  render() {
    const { ValueCell, SortedHeaderCell } = this;
    const { filters, sims, hiddenColumns } = this.state;
    const {
      store: { zoneId },
      exchangeType,
      onRefresh,
    } = this.props;

    let simRows;
    if (sims) {
      simRows = sims.map(sim => {
        const config = getBotConfig(sim);
        if (config.positiveFailSafe) {
          config.positiveFailSafe_TriggerPct = config.positiveFailSafe.triggerPct;
          config.positiveFailSafe_PeakChangePct = config.positiveFailSafe.peakChangePct;
        } else {
          config.positiveFailSafe_TriggerPct = null;
          config.positiveFailSafe_PeakChangePct = null;
        }
        if (config.rsi) {
          config.rsi_timeFrame = config.rsi.timeFrame;
          config.rsi_buyTreshold = config.rsi.buyTreshold;
          config.rsi_afterMinutes = config.rsi.afterMinutes;
          config.rsi_sellTreshold = config.rsi.sellTreshold;
          config.rsi_afterMinutesTreshold = config.rsi.afterMinutesTreshold;
          config.rsi_rsStrategy =
            config.rsi.absoluteRS !== undefined ? (config.rsi.absoluteRS ? "absolute" : "percentage") : "unknown";
        } else {
          config.rsi_timeFrame = null;
          config.rsi_buyTreshold = null;
          config.rsi_afterMinutes = null;
          config.rsi_sellTreshold = null;
          config.rsi_afterMinutesTreshold = null;
          config.rsi_rsStrategy = null;
        }

        sim.runDays =
          (instantFromApi(sim.toDate).epochSecond() - instantFromApi(sim.fromDate).epochSecond()) / (60 * 60 * 24);
        sim.profitRelative = sim.profitTotal / config.tradeTotal;
        sim.profitRelativeDay = Math.pow(1 + sim.profitRelative, 1 / sim.runDays) - 1;
        sim.closedTradesCountDay = sim.closedTradesCount / sim.runDays;
        sim.createdAtDisplay = formatDateTimeFromApi(sim.createdAt, zoneId);
        sim.fromDateDisplay = formatDateFromApi(sim.fromDate, zoneId);
        sim.toDateDisplay = formatDateFromApi(sim.toDate, zoneId);

        if (
          !filters.every(filter => {
            const currentValue = R.path(filter.columnKey.split("."))(sim);
            switch (filter.mode) {
              case "==":
                return currentValue === filter.value;
              case "<>":
                return currentValue !== filter.value;
              case "<":
                return currentValue < filter.value;
              case "<=":
                return currentValue <= filter.value;
              case ">":
                return currentValue > filter.value;
              case ">=":
                return currentValue >= filter.value;

              default:
                throw Error("Unsupported mode " + filter.mode);
            }
          }) ||
          (this.state.favoriteOnly && !this.state.ratings[sim.id])
        ) {
          return null;
        }

        const active = this.state.selSim && sim.id === this.state.selSim.id;

        return (
          <Table.Row
            key={sim.id}
            onClick={() => this.setState({ selSim: sim })}
            style={{ cursor: "pointer", textAlign: "center", whiteSpace: "nowrap" }}
            active={active}
          >
            <Table.Cell>
              <Rating
                icon="star"
                maxRating={1}
                onRate={(event, data) => this.setState({ ratings: { ...this.state.ratings, [sim.id]: data.rating } })}
                rating={this.state.ratings[sim.id]}
              />
              {sim.id.substring(0, 4)}
            </Table.Cell>
            {!hiddenColumns.includes("botId") && <Table.Cell>{sim.botId && sim.botId.substring(0, 4)}</Table.Cell>}

            {!hiddenColumns.includes("profitRelative") && (
              <Table.Cell active={active} positive={sim.profitTotal > 0} negative={sim.profitTotal < 0}>
                {formatPercent(sim.profitRelative)}
              </Table.Cell>
            )}
            {!hiddenColumns.includes("profitRelativeDay") && (
              <Table.Cell active={active} positive={sim.profitTotal > 0} negative={sim.profitTotal < 0}>
                {formatPercent(sim.profitRelativeDay)}
              </Table.Cell>
            )}
            {!hiddenColumns.includes("closedTrades.length") && <Table.Cell>{sim.closedTradesCount}</Table.Cell>}
            <ValueCell columnKey="type" sim={sim}>
              {sim.type}
            </ValueCell>
            <ValueCell columnKey="config.maxOpenTrades" sim={sim}>
              {config.maxOpenTrades}
            </ValueCell>
            <ValueCell columnKey="config.maxCurrencyTrades" sim={sim}>
              {config.maxCurrencyTrades}
            </ValueCell>
            <ValueCell columnKey="period" sim={sim}>
              {sim.period}s
            </ValueCell>
            <ValueCell columnKey="config.currencyPair" sim={sim}>
              <CurrencyPair exchangeType={sim.exchangeType} currencyPair={config.currencyPair} />
            </ValueCell>
            <ValueCell columnKey="config.sticksCount" sim={sim}>
              {config.sticksCount} * {config.sticksPeriod}
            </ValueCell>
            <ValueCell columnKey="config.belowAvgBuySignal" sim={sim}>
              {config.belowAvgBuySignal && config.belowAvgBuySignal.toFixed(2) + "%"}
            </ValueCell>
            <ValueCell columnKey="config.peakChangePct" sim={sim}>
              {config.peakChangePct && config.peakChangePct.toFixed(2) + "%"}
            </ValueCell>
            <ValueCell columnKey="config.minReturn" sim={sim}>
              {config.minReturn && config.minReturn.toFixed(2) + "%"}
            </ValueCell>

            <ValueCell columnKey="config.positiveFailSafe.triggerPct" sim={sim}>
              {config.positiveFailSafe &&
                config.positiveFailSafe.triggerPct &&
                config.positiveFailSafe.triggerPct.toFixed(2) + "%"}
            </ValueCell>
            <ValueCell columnKey="config.positiveFailSafe.peakChangePct" sim={sim}>
              {config.positiveFailSafe &&
                config.positiveFailSafe.peakChangePct &&
                config.positiveFailSafe.peakChangePct.toFixed(2) + "%"}
            </ValueCell>

            <ValueCell columnKey="config.placeOrderAfterMinutes" sim={sim}>
              {config.placeOrderAfterMinutes}
            </ValueCell>
            <ValueCell columnKey="config.forceSellAfterMinutes" sim={sim}>
              {config.forceSellAfterMinutes}
            </ValueCell>
            <ValueCell columnKey="config.volumeTreshold" sim={sim}>
              {config.volumeTreshold}
            </ValueCell>
            <ValueCell columnKey="config.highGrowthTresholdPct" sim={sim}>
              {config.highGrowthTresholdPct}
            </ValueCell>
            <ValueCell columnKey="config.highGrowthHours" sim={sim}>
              {config.highGrowthHours}
            </ValueCell>
            <ValueCell columnKey="config.rsi.timeFrame" sim={sim}>
              {config.rsi && config.rsi.timeFrame}
            </ValueCell>
            <ValueCell columnKey="config.rsi.buyTreshold" sim={sim}>
              {config.rsi && config.rsi.buyTreshold}
            </ValueCell>
            <ValueCell columnKey="config.rsi.sellTreshold" sim={sim}>
              {config.rsi && config.rsi.sellTreshold}
            </ValueCell>
            <ValueCell columnKey="config.rsi.afterMinutes" sim={sim}>
              {config.rsi && config.rsi.afterMinutes}
            </ValueCell>
            <ValueCell columnKey="config.rsi.afterMinutesTreshold" sim={sim}>
              {config.rsi && config.rsi.afterMinutesTreshold}
            </ValueCell>
            <ValueCell columnKey="config.rsi.absoluteRS" sim={sim}>
              {config.rsi && config.rsi.absoluteRS !== undefined
                ? config.rsi.absoluteRS
                  ? "absolute"
                  : "percentage"
                : ""}
            </ValueCell>
            {!hiddenColumns.includes("fromDate.epochSecond") && (
              <ValueCell columnKey="fromDateDisplay" sim={sim}>
                {sim.fromDateDisplay}
              </ValueCell>
            )}
            {!hiddenColumns.includes("toDate.epochSecond") && (
              <ValueCell columnKey="toDateDisplay" sim={sim}>
                {sim.toDateDisplay}
              </ValueCell>
            )}
            {!hiddenColumns.includes("createdAt.epochSecond") && (
              <ValueCell columnKey="createdAtDisplay" sim={sim}>
                {sim.createdAtDisplay}
              </ValueCell>
            )}
          </Table.Row>
        );
      });
    }

    const chartsButton = (
      <Button
        content="Charts"
        icon="chart area"
        disabled={!sims || !sims.length}
        toggle
        active={this.state.charts}
        onClick={() => {
          this.setState({ charts: !this.state.charts });
        }}
      />
    );

    if (this.state.charts) {
      return (
        <div>
          {chartsButton}
          <SimsCharts sims={sims} />
        </div>
      );
    }

    return (
      <div>
        {chartsButton}
        <div style={{ padding: 15, paddingBottom: 0 }}>
          <Label.Group>
            {filters.map((filter, i) => (
              <Label key={i} size="large">
                {filter.columnKey} {filter.mode} {filter.value}
                <Icon name="close" onClick={() => this.removeFilter(filter)} />
              </Label>
            ))}
          </Label.Group>
        </div>
        {Boolean(hiddenColumns.length) && (
          <div style={{ margin: "5px 15px" }}>
            Hidden columns:{" "}
            {hiddenColumns.map(columnKey => (
              <span
                key={columnKey}
                onClick={() => {
                  const newHiddenColumns = hiddenColumns.filter(c => c !== columnKey);
                  localStorage.setItem("sims-hidden-columns", JSON.stringify(newHiddenColumns));
                  this.setState({ hiddenColumns: newHiddenColumns });
                }}
                style={{ cursor: "pointer", margin: "0 5px" }}
              >
                {columnKey}
              </span>
            ))}
          </div>
        )}
        <div style={{ overflowY: "auto" }}>
          <Table sortable unstackable style={{ whiteSpace: "nowrap" }}>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Id</Table.HeaderCell>
                <SortedHeaderCell columnKey="botId">
                  Bot<br />Id
                </SortedHeaderCell>
                <SortedHeaderCell columnKey="profitRelative">
                  Closed Profit<br />% of init
                </SortedHeaderCell>
                <SortedHeaderCell columnKey="profitRelativeDay">
                  Closed<sup>^1</sup>/<sub>days</sub>
                </SortedHeaderCell>
                <SortedHeaderCell columnKey="closedTrades.length">
                  Closed<br />Trades
                </SortedHeaderCell>
                <SortedHeaderCell columnKey="type">Type</SortedHeaderCell>
                <SortedHeaderCell columnKey="config.maxOpenTrades">
                  Max<br />Trades
                </SortedHeaderCell>
                <SortedHeaderCell columnKey="config.maxCurrencyTrades">
                  Max<br />Currency<br />Trades
                </SortedHeaderCell>
                <SortedHeaderCell columnKey="period">Period</SortedHeaderCell>
                <SortedHeaderCell columnKey="config.currencyPair">
                  Currency<br />Pair
                </SortedHeaderCell>
                <SortedHeaderCell columnKey="config.sticksCount">Sticks</SortedHeaderCell>
                <SortedHeaderCell columnKey="config.belowAvgBuySignal">
                  Buy<br />Signal
                </SortedHeaderCell>
                <SortedHeaderCell columnKey="config.peakChangePct">
                  Peak<br />Change
                </SortedHeaderCell>
                <SortedHeaderCell columnKey="config.minReturn">Min Return</SortedHeaderCell>

                <SortedHeaderCell columnKey="config.positiveFailSafe.triggerPct">
                  positive<br />FailSafe<br />triggerPct
                </SortedHeaderCell>
                <SortedHeaderCell columnKey="config.positiveFailSafe.peakChangePct">
                  positive<br />FailSafe<br />peakChangePct
                </SortedHeaderCell>

                <SortedHeaderCell columnKey="config.placeOrderAfterMinutes">
                  Place Order<br />after min.
                </SortedHeaderCell>
                <SortedHeaderCell columnKey="config.forceSellAfterMinutes">
                  Force Sell<br />after min.
                </SortedHeaderCell>
                <SortedHeaderCell columnKey="config.volumeTreshold">
                  Volume<br />Treshold
                </SortedHeaderCell>
                <SortedHeaderCell columnKey="config.highGrowthTresholdPct">
                  High Growth<br />Treshold
                </SortedHeaderCell>
                <SortedHeaderCell columnKey="config.highGrowthHours">
                  High Growth<br />Hours
                </SortedHeaderCell>

                <SortedHeaderCell columnKey="config.rsi.timeFrame">
                  RSI<br />timeFrame
                </SortedHeaderCell>
                <SortedHeaderCell columnKey="config.rsi.buyTreshold">
                  RSI<br />buyTreshold
                </SortedHeaderCell>
                <SortedHeaderCell columnKey="config.rsi.sellTreshold">
                  RSI<br />sellTreshold
                </SortedHeaderCell>
                <SortedHeaderCell columnKey="config.rsi.afterMinutes">
                  RSI<br />afterMinutes
                </SortedHeaderCell>
                <SortedHeaderCell columnKey="config.rsi.afterMinutesTreshold">
                  RSI<br />afterMinutesTreshold
                </SortedHeaderCell>
                <SortedHeaderCell columnKey="config.rsi.absoluteRS">
                  RSI<br />RS strategy
                </SortedHeaderCell>

                <SortedHeaderCell columnKey="fromDate.epochSecond">From</SortedHeaderCell>
                <SortedHeaderCell columnKey="toDate.epochSecond">To</SortedHeaderCell>
                <SortedHeaderCell columnKey="createdAt.epochSecond">Created at</SortedHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>{simRows}</Table.Body>
          </Table>
        </div>

        <div>
          <Button
            content="Refresh"
            icon="refresh"
            onClick={() => {
              this.load();
              if (onRefresh) onRefresh();
            }}
            loading={this.state.loading}
          />

          <Button
            content="Favorite Only"
            icon="star"
            toggle
            active={this.state.favoriteOnly}
            onClick={() => this.setState({ favoriteOnly: !this.state.favoriteOnly })}
          />
        </div>

        {this.state.selSim && (
          <Segment>
            <Sim ref={i => (this.simComponent = i)} sim={this.state.selSim} exchangeType={exchangeType} />
          </Segment>
        )}
      </div>
    );
  }
}

export default Sims;



// WEBPACK FOOTER //
// ./src/sim/Sims.js